// Pure utility helpers for the Offers page.
// No React imports — safe to use anywhere.

import { genCopy } from './notifications/genericNotificationCopy';

export const formatPrice = (value) => {
  const num = typeof value === 'string' ? parseFloat(value.replace(/[^0-9.]/g, '')) : value;
  if (isNaN(num)) return '$0';
  return '$' + num.toLocaleString('en-US', { maximumFractionDigits: 0 });
};

/**
 * Parse a date-only value (`YYYY-MM-DD`) as LOCAL midnight.
 *
 * `new Date('2026-11-04')` is specified to parse as UTC midnight, so west of
 * Greenwich it formats as the 3rd — a closing date the buyer picked renders a
 * day early for everyone in the US. Values that carry a time component are
 * left to the normal Date parser.
 *
 * @param {string|Date} value
 * @returns {Date|null} local-midnight Date, or null when unparseable
 */
export const parseDateOnly = (value) => {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value).trim());
  if (match) {
    const [, y, m, d] = match;
    return new Date(Number(y), Number(m) - 1, Number(d));
  }
  const parsed = new Date(value);
  return isNaN(parsed.getTime()) ? null : parsed;
};

/**
 * Format a Date as `YYYY-MM-DD` in LOCAL terms — the inverse of parseDateOnly.
 *
 * `date.toISOString().split('T')[0]` converts to UTC first, so any evening in
 * a western timezone yields tomorrow's date. That is how the "45 days" closing
 * chip came to produce a date the review step then described as "46 days from
 * today": the chip counted in UTC, the review counted from local midnight.
 *
 * @param {Date} date
 * @returns {string} `YYYY-MM-DD` in the viewer's own timezone
 */
export const toDateOnly = (date) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

/**
 * `YYYY-MM-DD` for `days` from today, in LOCAL terms.
 *
 * Local, not UTC: toISOString() shifts the date forward for any evening west
 * of Greenwich, so the "45 days" closing chip used to emit today+46 in local
 * terms and the review step then labelled it "46 days from today".
 */
export const dateOnlyInDays = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return toDateOnly(d);
};

/** Format a date-only value for display, without the UTC shift. */
export const formatDateOnly = (value, fallback = '—') => {
  const date = parseDateOnly(value);
  return date ? date.toLocaleDateString() : fallback;
};

export const relativeTime = (dateStr) => {
  if (!dateStr) return '';
  const now = new Date();
  const then = new Date(dateStr);
  const diffMs = now - then;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay === 1) return 'Yesterday';
  if (diffDay < 7) return `${diffDay}d ago`;
  const diffWeek = Math.floor(diffDay / 7);
  if (diffWeek < 4) return `${diffWeek}w ago`;
  return then.toLocaleDateString();
};

export const getNotifDateGroup = (dateStr) => {
  const now = new Date();
  const then = new Date(dateStr);
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const thenDay = new Date(then.getFullYear(), then.getMonth(), then.getDate());
  const diffDay = Math.floor((today - thenDay) / 86400000);
  if (diffDay === 0) return 'Today';
  if (diffDay === 1) return 'Yesterday';
  if (diffDay < 7) return 'This Week';
  return 'Older';
};

export const FORM_STEPS = [
  { key: 'pricing', label: 'Pricing' },
  { key: 'financing', label: 'Financing' },
  { key: 'terms', label: 'Terms' },
  { key: 'review', label: 'Review' }
];

export const OFFER_STATUSES = ['pending', 'countered', 'accepted', 'rejected', 'withdrawn', 'expired', 'closed'];

const STATUS_META = {
  pending: {
    badgeValue: 'pending',
    label: 'Pending',
    icon: '⏳',
    primaryAction: { buyer: 'withdraw', seller: 'counter' },
    // Candidates only: getCardActions drops whatever isn't the viewer's turn
    // (a pending offer is the seller's move, so the buyer can only withdraw).
    nextActions: { buyer: ['counter', 'withdraw'], seller: ['accept', 'counter', 'reject'] },
    isTerminal: false,
  },
  countered: {
    badgeValue: 'countered',
    label: 'Countered',
    icon: '🔄',
    primaryAction: { buyer: 'accept', seller: 'accept' },
    nextActions: { buyer: ['accept', 'counter', 'withdraw'], seller: ['accept', 'counter', 'reject'] },
    isTerminal: false,
  },
  accepted: {
    badgeValue: 'accepted',
    label: 'Accepted',
    icon: '✅',
    primaryAction: { buyer: 'view', seller: 'view' },
    nextActions: { buyer: ['view'], seller: ['view'] },
    isTerminal: true,
  },
  rejected: {
    badgeValue: 'rejected',
    label: 'Rejected',
    icon: '❌',
    primaryAction: null,
    nextActions: { buyer: [], seller: [] },
    isTerminal: true,
  },
  withdrawn: {
    badgeValue: 'withdrawn',
    label: 'Withdrawn',
    icon: '🚫',
    primaryAction: null,
    nextActions: { buyer: [], seller: [] },
    isTerminal: true,
  },
  expired: {
    badgeValue: 'expired',
    label: 'Expired',
    icon: '⏰',
    primaryAction: null,
    nextActions: { buyer: [], seller: [] },
    isTerminal: true,
  },
  // Closed by cutthe6.com, not by either party (legal copy §15.3): today only
  // when the seller deleted the listing. Never "rejected", never "withdrawn"
  // as if by the buyer. Read-only: no accept, counter, reject or withdraw.
  closed: {
    badgeValue: 'closed',
    label: 'Closed',
    icon: '📁',
    primaryAction: null,
    nextActions: { buyer: [], seller: [] },
    isTerminal: true,
  },
};

export const getStatusMeta = (status) => STATUS_META[status] || STATUS_META.pending;

/**
 * An accepted offer whose transaction was recorded as ended keeps the status
 * `accepted`; the API flags it `deal_ended`. Cards and filters treat it as
 * ended, not accepted (QA 2026-10-16). The workspace stays viewable.
 */
export const DEAL_ENDED = 'deal_ended';
STATUS_META[DEAL_ENDED] = {
  badgeValue: 'closed',
  label: 'Deal ended',
  icon: '📁',
  primaryAction: { buyer: 'view', seller: 'view' },
  nextActions: { buyer: ['view'], seller: ['view'] },
  isTerminal: true,
};

/** The status a card shows: `deal_ended` for an accepted offer whose deal ended. */
export const displayStatus = (offer) => (
  offer?.status === 'accepted' && offer?.deal_ended ? DEAL_ENDED : offer?.status
);

/** Statuses where nobody can act on the offer any more. */
export const TERMINAL_OFFER_STATUSES = Object.keys(STATUS_META).filter((s) => STATUS_META[s].isTerminal && s !== 'accepted');

/** `offers.closed_reason` when the seller deleted the listing (backend OFFER_CLOSED_REASON_LISTING_DELETED). */
export const CLOSED_REASON_LISTING_DELETED = 'listing_deleted';

const statusOf = (offer) => String(offer?.status || offer?.offer_status || '').toLowerCase();

/** True for an offer cutthe6.com closed (status `closed`). */
export const isOfferClosed = (offer) => statusOf(offer) === 'closed';

/**
 * True when the offer was closed because the seller deleted the listing. The
 * offers API sends `close_reason` and `listing_deleted`; a `closed` offer with
 * no reason is treated the same, since that is the only reason the platform
 * closes an offer today.
 */
export function isClosedByListingDelete(offer) {
  if (!isOfferClosed(offer)) return false;
  const reason = offer.close_reason ?? offer.closed_reason ?? null;
  return offer.listing_deleted === true || reason === CLOSED_REASON_LISTING_DELETED || reason == null;
}

/**
 * The status line for a closed offer: legal's
 * `GEN.notification.offer_closed_listing_deleted.text` (copy §15.3), else null.
 * The email body isn't used: it needs the deletion time, which the offer
 * doesn't carry.
 */
export function offerClosedStatusText(offer) {
  return isClosedByListingDelete(offer) ? genCopy('GEN.notification.offer_closed_listing_deleted.text') : null;
}

export const PRIMARY_ACTION_LABELS = {
  accept: 'Negotiate →',
  counter: 'Negotiate →',
  reject: 'Negotiate →',
  withdraw: 'Negotiate →',
  view: 'View in Workspace →',
};

export const getRoleTurn = (offer, currentRole) => {
  if (!offer || !currentRole) return null;
  const status = offer.status;
  if (['accepted', ...TERMINAL_OFFER_STATUSES].includes(status)) return null;

  if (status === 'pending') {
    // Buyer submitted; seller is up.
    if (currentRole === 'seller') return 'you';
    if (currentRole === 'buyer') return 'them';
    return null;
  }

  if (status === 'countered') {
    // Whoever countered last is waiting; the OPPOSITE party is up.
    const lastActor = offer.last_actor_role;
    if (lastActor === 'buyer') {
      // Buyer countered → seller's turn
      return currentRole === 'seller' ? 'you' : 'them';
    }
    if (lastActor === 'seller') {
      // Seller countered → buyer's turn
      return currentRole === 'buyer' ? 'you' : 'them';
    }
    // Fallback: assume seller countered → buyer's turn.
    return currentRole === 'buyer' ? 'you' : 'them';
  }

  return null;
};

/**
 * Whether `role` can take `action` on `offer` right now. The same turn rule the
 * NegotiationWorkspace uses: accept and counter only on your turn, reject only
 * for the seller on their turn, withdraw only for the buyer while the offer is
 * still live. `view` is always available on a non-terminal or accepted offer.
 */
export const canRoleTakeAction = (offer, role, action) => {
  const status = statusOf(offer);
  const live = status === 'pending' || status === 'countered';
  const myTurn = getRoleTurn({ ...offer, status }, role) === 'you';
  switch (action) {
    case 'accept':
    case 'counter':
      return live && myTurn;
    case 'reject':
      return live && role === 'seller' && myTurn;
    case 'withdraw':
      return live && role === 'buyer';
    case 'view':
      return live || status === 'accepted';
    default:
      return false;
  }
};

/**
 * The offer card's actions for `role`: the primary button and the "More
 * actions" negotiation items, filtered to what the viewer can do right now.
 * When the status's preferred primary action isn't the viewer's to take, the
 * first action they can take is promoted, else "View in Workspace".
 *
 * @returns {{ primary: string|null, menu: string[] }}
 */
export const getCardActions = (offer, role) => {
  const meta = getStatusMeta(statusOf(offer));
  const can = (k) => canRoleTakeAction(offer, role, k);
  const candidates = (meta?.nextActions?.[role] || []).filter(can);
  const preferred = meta?.primaryAction?.[role] || null;
  let primary = null;
  if (preferred && can(preferred)) primary = preferred;
  else if (meta?.primaryAction) primary = candidates[0] || (can('view') ? 'view' : null);
  return { primary, menu: candidates.filter((k) => k !== primary) };
};

export const getStatusFilterCounts = (offers = []) => {
  const counts = {
    all: offers.length,
    pending: 0,
    countered: 0,
    accepted: 0,
    rejected: 0,
    withdrawn: 0,
    expired: 0,
    closed: 0,
    deal_ended: 0,
    other: 0,
  };
  for (const o of offers) {
    const status = displayStatus(o);
    if (counts[status] != null) counts[status] += 1;
  }
  counts.other = counts.withdrawn + counts.expired + counts.rejected + counts.closed + counts.deal_ended;
  return counts;
};

export const sortOffers = (offers = [], sortKey = 'newest') => {
  const arr = [...offers];
  switch (sortKey) {
    case 'oldest':
      return arr.sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
    case 'priceDesc':
      return arr.sort((a, b) => (Number(b.offer_price) || 0) - (Number(a.offer_price) || 0));
    case 'priceAsc':
      return arr.sort((a, b) => (Number(a.offer_price) || 0) - (Number(b.offer_price) || 0));
    case 'status': {
      const order = OFFER_STATUSES;
      return arr.sort((a, b) => {
        const ai = order.indexOf(a.status);
        const bi = order.indexOf(b.status);
        const ax = ai === -1 ? order.length : ai;
        const bx = bi === -1 ? order.length : bi;
        if (ax !== bx) return ax - bx;
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      });
    }
    case 'newest':
    default:
      return arr.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
  }
};
